import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { When } from "react-if";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
  EMAIL_PERFORMANCE_DAYS,
  EmailPerformanceDays,
  IEmailTemplateStats,
  IEmailVariationStats,
} from "shared";

import { Button } from "../../../common/Atoms/Button";
import { Select } from "../../../common/Atoms/Select";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { formatAmount } from "../../../common/lib/helpers";
import { useFetchEmailPerformanceQuery } from "../../services/api/emailPerformanceApi/emailPerformance";
import { LoadingOverlay } from "../Molecules/LoadingOverlay";
import { templateGroupLabels } from "../Settings/EmailTemplates/constants";

import { OpenTimeHeatmap } from "./OpenTimeHeatmap";
import { SendTimeHeatmap } from "./SendTimeHeatmap";
import { TemplateStatsTable } from "./TemplateStatsTable";
import { TrackingHelpPopover } from "./TrackingHelpPopover";
import {
  clickRate,
  formatCount,
  formatPct,
  IDelta,
  openRate,
  paidWithin7Rate,
  percentDelta,
  pointsDelta,
  spamRate,
  templateLabel,
  toCsv,
} from "./stats";

/** Green when the change is good, red when bad; a rise is bad for lower-is-better metrics */
function deltaClass(k: IKpi) {
  if (!k.delta || k.delta.direction === `flat`) {
    return `text-gray-500`;
  }

  const good = k.lowerIsBetter ? k.delta.direction === `down` : k.delta.direction === `up`;

  return good ? `text-green-700` : `text-paidniceRed-600`;
}

/**
 * 26px, shrunk to fit the card width for long values like large currency amounts. A semibold tabular
 * character is at most about 0.62em wide, and cqi is relative to the card (an @container).
 */
function kpiValueFontSize(value: string) {
  return `min(26px, ${(100 / (Math.max(value.length, 1) * 0.62)).toFixed(2)}cqi)`;
}

const rangeOptions = EMAIL_PERFORMANCE_DAYS.map(days => ({ label: `Last ${days} days`, value: days }));

/** Templates with fewer sends than this are left out of the highlights */
const MIN_HIGHLIGHT_SENDS = 10;

interface IKpi {
  label: string;
  value: string;
  delta: IDelta | null;
  /** Show the tracking caveat popover next to the label */
  help?: boolean;
  /** Colour a rise red and a fall green */
  lowerIsBetter?: boolean;
}

interface IHighlight {
  label: string;
  name: string;
  detail: string;
}

export function EmailPerformance() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const [days, setDays] = useState<EmailPerformanceDays>(90);
  // undefined = organisation timezone (server default)
  const [timezone, setTimezone] = useState<string | undefined>();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useFetchEmailPerformanceQuery({ organisationId: organisationId!, days, timezone }, {
    skip: !organisationId,
  });

  const kpis = useMemo<IKpi[]>(() => {
    if (!data) {
      return [];
    }

    const { totals: t, previousTotals: p, homeCurrency } = data;

    return [
      { label: `Emails sent`, value: formatCount(t.sent), delta: percentDelta(t.sent, p.sent) },
      { label: `Open rate`, value: formatPct(openRate(t)), delta: pointsDelta(openRate(t), openRate(p)), help: true },
      { label: `Click rate`, value: formatPct(clickRate(t)), delta: pointsDelta(clickRate(t), clickRate(p)), help: true },
      { label: `Spam rate`, value: formatPct(spamRate(t)), delta: pointsDelta(spamRate(t), spamRate(p)), lowerIsBetter: true },
      { label: `Paid within 7 days`, value: formatPct(paidWithin7Rate(t)), delta: pointsDelta(paidWithin7Rate(t), paidWithin7Rate(p)) },
      { label: `Collected after email`, value: formatAmount(t.collectedHomeCents, homeCurrency), delta: percentDelta(t.collectedHomeCents, p.collectedHomeCents) },
    ];
  }, [data]);

  const highlights = useMemo<IHighlight[]>(() => {
    if (!data) {
      return [];
    }

    const named = data.templates.filter(t => t.templateName && t.sent >= MIN_HIGHLIGHT_SENDS);
    const rated = named.filter(t => openRate(t) !== null);

    if (rated.length === 0) {
      return [];
    }

    const avgOpen = openRate(data.totals);
    const byOpen = [...rated].sort((a, b) => openRate(b)! - openRate(a)!);
    const byCollected = [...named].sort((a, b) => b.collectedHomeCents - a.collectedHomeCents);
    const out: IHighlight[] = [];

    const best = byOpen[0];
    const bestOpen = openRate(best)!;

    out.push({
      label: `Highest open rate`,
      name: templateLabel(best),
      detail: avgOpen === null
        ? `${formatPct(bestOpen)} open rate.`
        : `${formatPct(bestOpen)} open rate, ${Math.abs(bestOpen - avgOpen).toFixed(0)} pts ${bestOpen >= avgOpen ? `above` : `below`} your average.`,
    });

    const richest = byCollected[0];

    if (richest.collectedHomeCents > 0) {
      out.push({
        label: `Most revenue collected`,
        name: templateLabel(richest),
        detail: `${formatAmount(richest.collectedHomeCents, data.homeCurrency)} collected, with ${formatPct(paidWithin7Rate(richest))} of invoices paid within 7 days.`,
      });
    }

    if (byOpen.length > 1) {
      const worst = byOpen[byOpen.length - 1];

      out.push({
        label: `Lowest engagement`,
        name: templateLabel(worst),
        detail: `${formatPct(openRate(worst))} open rate. Try a subject line that names the invoice number and amount.`,
      });
    }

    return out;
  }, [data]);

  const exportCsv = () => {
    if (!data) {
      return;
    }

    const row = (t: IEmailTemplateStats, variation?: IEmailVariationStats) => {
      const m = variation ?? t;

      return [
        templateLabel(t),
        variation ? (variation.isOriginal ? `Original` : variation.name) : ``,
        t.templateType ? (templateGroupLabels[t.templateType] ?? t.templateType) : ``,
        m.subject,
        m.sent,
        m.delivered,
        m.opened,
        m.clicked,
        m.failed,
        m.spam,
        m.invoiceEmails,
        m.paidWithin7,
        m.paidWithin14,
        m.paidWithin30,
        m.medianDaysToPay,
        (m.collectedHomeCents / 100).toFixed(2),
      ];
    };
    const csv = toCsv([
      [`Template`, `Variation`, `Type`, `Subject`, `Sent`, `Delivered`, `Opened`, `Clicked`, `Failed`, `Spam reports`, `Invoice emails`, `Paid within 7 days`, `Paid within 14 days`, `Paid within 30 days`, `Median days to pay`, `Collected (${data.homeCurrency})`],
      ...data.templates.flatMap(t => [row(t), ...(t.variations ?? []).map(v => row(t, v))]),
    ]);
    const url = URL.createObjectURL(new Blob([csv], { type: `text/csv` }));
    const a = document.createElement(`a`);
    a.href = url;
    a.download = `email-performance-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasData = !!data && data.totals.sent > 0;

  return (
    <LoadingOverlay loading={ isLoading || isFetching }>
      <div className={ `mx-auto flex max-w-[1200px] flex-col gap-6` }>
        <div className={ `flex flex-wrap items-end justify-between gap-4 rounded-lg border border-gray-200 bg-white p-5` }>
          <div className={ `flex flex-col gap-1` }>
            <h1 className={ `text-2xl font-semibold text-paidniceGreen-800` }>{ `Email performance` }</h1>
            <div className={ `text-sm text-gray-500` }>{ `How your email templates perform, from open to payment.` }</div>
          </div>
          <div className={ `flex items-end gap-3` }>
            <div className={ `w-[180px]` }>
              <Select
                options={ rangeOptions }
                selected={ days }
                onChange={ ({ value }) => setDays(Number(value) as EmailPerformanceDays) }
                size={ `sm` }
              />
            </div>
            <Button color={ `gray` }
              onClick={ exportCsv }
              disabled={ !hasData }>
              { `Export CSV` }
            </Button>
          </div>
        </div>

        <When condition={ isError && !isFetching }>
          <div className={ `flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white text-center` }>
            <Paragraph variant={ `secondary` }>
              { `We couldn't load your email performance data. Please try again.` }
            </Paragraph>
            <Button color={ `paidniceGreen` }
              onClick={ () => refetch() }>
              { `Retry` }
            </Button>
          </div>
        </When>

        <When condition={ !!data && !hasData }>
          <div className={ `flex min-h-[240px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-white text-center` }>
            <EnvelopeIcon className={ `mx-auto h-12 w-12 text-gray-300` } />
            <Paragraph variant={ `secondary` }>
              { `No emails were sent in the last ${days} days. Performance will appear here once reminders start going out.` }
            </Paragraph>
          </div>
        </When>

        <When condition={ hasData }>
          <div className={ `grid gap-3` }
            style={ { gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))` } }>
            { kpis.map(k => (
              <div key={ k.label }
                className={ `@container flex min-w-0 flex-col gap-1.5 rounded-lg border border-gray-200 bg-white p-4` }>
                <div className={ `flex items-center gap-1.5 text-[13px] text-gray-500` }>
                  { k.label }
                  <When condition={ !!k.help }>
                    <TrackingHelpPopover />
                  </When>
                </div>
                <div className={ `whitespace-nowrap font-semibold tracking-tight tabular-nums text-gray-900` }
                  style={ { fontSize: kpiValueFontSize(k.value) } }>
                  { k.value }
                </div>
                <When condition={ !!k.delta }>
                  <div className={ `text-xs ${deltaClass(k)}` }>
                    { k.delta?.label }
                    <span className={ `text-gray-500` }>{ ` vs previous ${days} days` }</span>
                  </div>
                </When>
              </div>
            )) }
          </div>

          <When condition={ highlights.length > 0 }>
            <div className={ `grid gap-3` }
              style={ { gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))` } }>
              { highlights.map(h => (
                <div key={ h.label }
                  className={ `flex flex-col gap-1.5 rounded-lg border border-paidniceGreen-100 bg-paidniceGreen-50 p-4` }>
                  <div className={ `text-xs font-semibold uppercase tracking-wide text-paidniceGreen-600` }>{ h.label }</div>
                  <div className={ `text-base font-semibold text-paidniceGreen-800` }>{ h.name }</div>
                  <div className={ `text-sm text-gray-700` }>{ h.detail }</div>
                </div>
              )) }
            </div>
          </When>

          { data && (
            <>
              <TemplateStatsTable
                organisationId={ organisationId! }
                templates={ data.templates }
                homeCurrency={ data.homeCurrency }
              />
              <SendTimeHeatmap sendTimes={ data.sendTimes }
                timezone={ data.timezone }
                onTimezoneChange={ setTimezone } />
              <OpenTimeHeatmap openTimes={ data.openTimes ?? [] }
                timezone={ data.timezone } />
            </>
          ) }
        </When>
      </div>
    </LoadingOverlay>
  );
}
